/**
 * Cost Centres — CRUD + P&L drill-down by cost centre.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, X, Layers } from 'lucide-react';
import { booksClient, getBooksTenant, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CostCentresPage = () => {
  const cur = getBooksTenant()?.base_currency;
  const [items, setItems] = useState([]);
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      booksClient.get('/cost-centres'),
      booksClient.get('/cost-centres/profit-loss'),
    ]).then(([a, b]) => { setItems(a.data.data || []); setPl(b.data.rows || []); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Analytics</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Cost Centres</h1>
          <div className="text-sm text-[#0b1a2e]/60 mt-1">Tag document lines with a cost centre to see per-vertical or per-project P&L.</div>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid="cc-new"
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
          <Plus className="w-3.5 h-3.5" /> New Cost Centre
        </button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <>
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Registered Cost Centres</div>
            {items.length === 0 ? (
              <div className="p-12 text-center text-[#0b1a2e]/40 italic">
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
                None yet. Add one to start tagging lines.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                  <tr><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Description</th><th className="text-left px-4 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.cost_centre_id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                      <td className="px-4 py-2">{c.name}</td>
                      <td className="px-4 py-2 text-xs text-[#0b1a2e]/60">{c.description || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${c.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200'}`}>{c.is_active ? 'active' : 'inactive'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pl && pl.length > 0 && (
            <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Profit & Loss by Cost Centre</div>
              <table className="w-full text-sm">
                <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                  <tr>
                    <th className="text-left px-4 py-2">Cost Centre</th>
                    <th className="text-right px-4 py-2">Income</th>
                    <th className="text-right px-4 py-2">Expense</th>
                    <th className="text-right px-4 py-2">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {pl.map((r) => (
                    <tr key={r.cost_centre} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs">{r.cost_centre === 'unassigned' ? <em className="text-[#0b1a2e]/50">unassigned</em> : r.cost_centre}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmt(r.income)}</td>
                      <td className="px-4 py-2 text-right font-mono text-[#0b1a2e]/70">{fmt(r.expense)}</td>
                      <td className={`px-4 py-2 text-right font-mono font-semibold ${r.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(r.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {openNew && <NewCC onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
    </div>
  );
};

const NewCC = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ code: '', name: '', description: '' });
  const submit = async () => {
    try {
      await booksClient.post('/cost-centres', form);
      toast({ title: 'Cost centre created' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>New Cost Centre</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <L label="Code"><input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="cc-code" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" /></L>
          <L label="Name"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="cc-name" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
          <L label="Description"><textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.code || !form.name} data-testid="cc-save"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
};

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default CostCentresPage;
